/**
 * Lazy Sentry bootstrap.
 *
 * `@sentry/react` is treated as an OPTIONAL runtime dependency — Sentry
 * is large and only needed in production. We dynamic-import it only
 * when `VITE_SENTRY_DSN` is set, wrap the import in a try/catch, and
 * gracefully fall back to console logging when the module isn't
 * installed. This keeps the dev dependency graph light while still
 * enabling Sentry in builds that bundle it.
 */

type SentryLike = {
  init: (options: Record<string, unknown>) => void;
  captureException: (error: unknown, hint?: Record<string, unknown>) => void;
};

let sentry: SentryLike | null = null;
let enabled = false;

export async function initObservability(): Promise<void> {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) {
    return;
  }
  try {
    const specifier = "@sentry/react";
    // Vite-ignored so the optional module isn't statically resolved at build
    // time; falls through to the catch below if it isn't installed.
    const mod = (await import(/* @vite-ignore */ specifier)) as SentryLike;
    mod.init({
      dsn,
      environment: import.meta.env.MODE,
      tracesSampleRate: Number(
        import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0.1,
      ),
    });
    sentry = mod;
    enabled = true;
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[observability] Sentry init skipped:", err);
    }
  }
}

export function captureError(
  error: unknown,
  context?: Record<string, unknown>,
): void {
  if (!enabled || !sentry) {
    if (import.meta.env.DEV) {
      console.error("[observability]", error, context);
    }
    return;
  }
  sentry.captureException(error, context ? { extra: context } : undefined);
}

/**
 * Whether a failed request is an answer its caller expects and draws itself: a query
 * or mutation says which HTTP statuses those are with `meta: { expectedStatuses }`
 * (a 404 for "nothing on record yet", a 409 the API refuses with its own sentence).
 * Such an answer is not an error to report. Anything else — another status, no
 * response at all, a `meta` of another shape — still is.
 */
/**
 * `meta` for a write whose refusal is an answer the page draws in the API's own words:
 * 409 (the state forbids it now: a limit, a conflict, a server that cannot be allowed)
 * and 422 (what was sent is refused as written). The page says it; the error tracker
 * is not told (browser pass BS-07).
 */
export const REFUSAL_IS_AN_ANSWER = { expectedStatuses: [409, 422] } as const;

export function isExpectedAnswer(error: unknown, meta: unknown): boolean {
  const expected = (meta as { expectedStatuses?: unknown } | null | undefined)?.expectedStatuses;
  if (!Array.isArray(expected)) return false;
  const status = (error as { response?: { status?: unknown } } | null | undefined)?.response?.status;
  return typeof status === "number" && expected.includes(status);
}

export function isObservabilityEnabled(): boolean {
  return enabled;
}
